import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import type { DashboardRoleItem } from "@/features/roles/types/role";
import { useCreateRole, useUpdateRole } from "@/features/roles/hooks/useRolesMutations";

const roleSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: "اسم الدور يجب أن يكون حرفين على الأقل" })
    .max(80, { message: "اسم الدور طويل جداً" }),
  description: z.string().max(500, { message: "الوصف طويل جداً" }).optional(),
});

type RoleFormValues = z.infer<typeof roleSchema>;

interface RoleFormPageProps {
  role?: DashboardRoleItem;
}

export function RoleFormPage({ role }: RoleFormPageProps) {
  const navigate = useNavigate();
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole(role?.code ?? "");

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: role?.name ?? "",
      description: role?.description ?? "",
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  async function onSubmit(data: RoleFormValues) {
    if (role) {
      await updateMutation.mutateAsync({
        name: data.name,
        description: data.description,
      });
      navigate(appPath(Pages.ROLES));
      return;
    }

    const created = await createMutation.mutateAsync({
      name: data.name,
      description: data.description,
    });
    navigate(
      `${appPath(Pages.USERS, "permissions")}?role=${encodeURIComponent(created.role)}`,
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mx-auto max-w-2xl space-y-6">
        <Card>
          <CardContent className="space-y-6 p-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>اسم الدور</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="مثال: مراجع مالي" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>الوصف (اختياري)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      placeholder="وصف مختصر لصلاحيات هذا الدور"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!role && (
              <p className="text-sm text-muted-foreground">
                بعد الإنشاء ستُوجَّه إلى صفحة الصلاحيات لتعيين نطاق هذا الدور على
                كل الدوائر.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button type="submit" disabled={isPending} className="flex-1">
            {role ? "تحديث الدور" : "إنشاء الدور"}
            {isPending && <Loader className="mr-2" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={isPending}
            onClick={() => navigate(appPath(Pages.ROLES))}
          >
            إلغاء
          </Button>
        </div>
      </form>
    </Form>
  );
}
